import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Form, FormGroup } from 'reactstrap';
import { connect } from 'react-redux';
import Immutable from 'immutable';
import { change, Field, formValueSelector, reduxForm } from 'redux-form';
import FormItem from '../../../common/forms/FormItem';
import TextInput from '../../../common/forms/input/TextInput';
import {
  INCIDENT_RECORD_TYPE,
  INCIDENT_SCOPE_TYPE,
} from '../../../../config/constants';
import FormFooter from '../../../common/forms/FormFooter';
import { isRequired } from '../../../../utils/validators';
import Select from '../../../common/forms/select/Select';
import AgencySelect from '../../../common/forms/select/AgencySelect';
import DatePicker from '../../../common/forms/input/DatePicker';
import Loader from '../../../common/Loader';
import DynamicForm from '../../../common/forms/DynamicForm';
import { generateIncidentRecordColumns } from '../../../../config/dynamicFormFields';

const selector = formValueSelector('IncidentRecordForm');

const IncidentRecordForm = ({
  handleSubmit,
  onSubmit,
  dispatchChange,
  loading,
  incidentForForm,
  incidentScope,
  handleIncidentRecordTypeChange,
  incidentRecordTypeValue,
  incidentForFormLoading,
  allowDuplicateNodes,
  label
}) => {
  const [nodePathsPerRow, setNodePathsPerRow] = useState({});
  const dynamicFormRef = useRef(null);

  const isItinerary = incidentScope === INCIDENT_SCOPE_TYPE.ITINERARY.value;
  const isAgency = incidentScope === INCIDENT_SCOPE_TYPE.AGENCY.value;
  const isAdministrative =
    incidentScope === INCIDENT_SCOPE_TYPE.ADMINISTRATIVE.value;

  useEffect(() => {
    if (!incidentRecordTypeValue) return;
    setNodePathsPerRow({});
    dispatchChange('IncidentRecordForm', 'incidentItems', [
      { _rowId: Date.now() },
    ]);
  }, [incidentRecordTypeValue]);

  const handleClickAdd = (fields) => {
    fields.push({ _rowId: Date.now() });
  };

  const handleClickRemove = (index, fields) => {
    setNodePathsPerRow((prev) => {
      const next = {};
      Object.keys(prev).forEach((key) => {
        const match = key.match(/incidentItems\[(\d+)\]/);
        if (!match) return;
        const rowIndex = parseInt(match[1], 10);
        if (rowIndex === index) return;
        if (rowIndex < index) {
          next[key] = prev[key];
        } else {
          const newKey = key.replace(
            `incidentItems[${rowIndex}]`,
            `incidentItems[${rowIndex - 1}]`,
          );
          next[newKey] = prev[key];
        }
      });
      return next;
    });

    fields.remove(index);
  };

  const handleNodePathChange = (fieldName, path) => {
    setNodePathsPerRow((prev) => ({ ...prev, [fieldName]: path }));
  };

  const options = () =>
    Object.values(INCIDENT_RECORD_TYPE).filter(
      (type) => type.incidentScopeType === incidentScope,
    ) || [];

  const nodes =
    !incidentForFormLoading && !incidentForForm.isEmpty()
      ? incidentForForm.get('nodes').toJS()
      : [];

  const itineraryFields = isItinerary && (
    <>
      <FormGroup row>
        <FormItem label="Tipo de Registro" required>
          <Field
            name="incidentRecordType"
            component={Select}
            placeholder="Tipo de Registro"
            options={options()}
            validate={[isRequired]}
            onChange={handleIncidentRecordTypeChange}
          />
        </FormItem>
      </FormGroup>
    </>
  );

  const agencyFields = (isAgency || isAdministrative) && (
    <>
      <FormGroup row>
        <FormItem label="Agencia" required>
          <Field
            name="agency"
            component={AgencySelect}
            onlyUserAgencies
            validate={[isRequired]}
          />
        </FormItem>
      </FormGroup>
    </>
  );

  const frecuencyFields = isAdministrative && (
    <FormGroup row>
        <FormItem label="Frecuencia" required>
          <Field
            name="incidentRecordType"
            component={Select}
            options={options()}
            validate={[isRequired]}
            onChange={handleIncidentRecordTypeChange}
          />
        </FormItem>
      </FormGroup>
  );

  const onHandleSubmit = (formValues) => {
    const { incidentItems } = formValues;
    const newFormValues = {
      ...formValues,
      incidentId: !incidentForForm.isEmpty() ? incidentForForm.get('id') : null,
      incidentScope: !incidentForForm.isEmpty()
        ? incidentForForm.get('incidentScope')
        : null,
      nodePathsPerRow,
      incidentItems,
    };
    onSubmit(newFormValues);
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <Form onSubmit={handleSubmit(onHandleSubmit)}>
      {itineraryFields}
      {agencyFields}
      {frecuencyFields}
      <FormGroup row>
        <FormItem label="Fecha" required>
          <Field
            name="dateString"
            component={DatePicker}
            validate={[isRequired]}
            disabled
          />
        </FormItem>
      </FormGroup>
      <FormGroup row>
        <FormItem label="Hora" required>
          <Field
            name="timeString"
            component={TextInput}
            type="time"
            placeholder="HH:mm"
            validate={[isRequired]}
            disabled
          />
        </FormItem>
      </FormGroup>

      {incidentForFormLoading && <Loader />}

      {!incidentForFormLoading && !incidentForForm.isEmpty() && (
        <DynamicForm
          ref={dynamicFormRef}
          key={`${incidentForForm.get('id')}-${
            incidentRecordTypeValue ? incidentRecordTypeValue.value : ''
          }`}
          name="incidentItems"
          initialValues={[1].map(() => ({ _rowId: Date.now() }))}
          onClickAdd={handleClickAdd}
          columns={generateIncidentRecordColumns(
            nodes,
            handleNodePathChange,
            nodePathsPerRow,
            allowDuplicateNodes,
            label
          )}
          showRemoveButton
          showAddButton
          title=""
          nestedTittle={false}
          onClickRemove={handleClickRemove}
        />
      )}

      <FormFooter saveButtonDisabled={loading} />
    </Form>
  );
};

IncidentRecordForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  dispatchChange: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  incidentForForm: PropTypes.instanceOf(Immutable.Map).isRequired,
  onSubmit: PropTypes.func.isRequired,
  incidentScope: PropTypes.string.isRequired,
  handleIncidentRecordTypeChange: PropTypes.func,
  incidentRecordTypeValue: PropTypes.shape({
    value: PropTypes.string,
  }),
  incidentForFormLoading: PropTypes.bool,
  allowDuplicateNodes: PropTypes.bool,
  label: PropTypes.string,
};

IncidentRecordForm.defaultProps = {
  loading: false,
  handleIncidentRecordTypeChange: undefined,
  incidentRecordTypeValue: null,
  incidentForFormLoading: false,
  allowDuplicateNodes: false,
  label: "Tipo de incidencia",
};

const mapStateToProps = (state) => ({
  incidentForForm: state.SupervisionControlUnit.Incident.getIn([
    'incidentForForm',
    'content',
  ]),
  incidentForFormLoading: state.SupervisionControlUnit.Incident.getIn([
    'incidentForForm',
    'loading',
  ]),
  loading: state.SupervisionControlUnit.IncidentRecord.getIn([
    'incidentRecordActivity',
    'loading',
  ]),
  incidentRecordTypeValue: selector(state, 'incidentRecordType'),
});

const mapDispatchToProps = {
  dispatchChange: change,
};

const form = reduxForm({
  form: 'IncidentRecordForm',
})(IncidentRecordForm);

export default connect(mapStateToProps, mapDispatchToProps)(form);
